import React, { useState, useEffect, useMemo } from 'react';
import { Property } from '@/types';
import { CienCuadrasLogoImg, SpinnerIcon, ExclamationTriangleIcon } from '@/components/Icons';
import { Footer } from '@/components/Footer';
import { getPropertyByIdApi } from '@/services/propertyApiClient';
import { SharedPropertyGallery } from '@/components/public/shared/SharedPropertyGallery';
import { SharedPropertyHeader } from '@/components/public/shared/SharedPropertyHeader';
import { SharedPropertyInfo } from '@/components/public/shared/SharedPropertyInfo';
import { BrokerBusinessCard } from '@/components/public/shared/BrokerBusinessCard';

interface SharedPropertyViewParams {
  propertyId: string;
  collectionName?: string;
}

const getYoutubeEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  const match = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : null;
};

export const SharedPropertyView: React.FC<SharedPropertyViewParams> = ({ propertyId, collectionName }) => {
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  useEffect(() => {
    const fetchProperty = async () => {
      setLoading(true);
      setError(null);
      try {
        const effectiveCollection = collectionName || new URLSearchParams(window.location.hash.split('?')[1] || '').get('collection') || 'c';
        const data = await getPropertyByIdApi(propertyId, effectiveCollection);
        setProperty(data);
        if (data.video_youtube_url) setActiveIndex(-1);
      } catch (err: any) {
        console.error('Error cargando inmueble:', err);
        setError('El inmueble solicitado no existe o ya no está disponible.');
      } finally {
        setLoading(false);
      }
    };
    if (propertyId) fetchProperty();
  }, [propertyId, collectionName]);

  const media = useMemo(() => {
    if (!property) return { images: [], video: null };
    const video = property.video_youtube_url ? { embedUrl: getYoutubeEmbedUrl(property.video_youtube_url) } : null;
    const images = (property.images || []).filter((url) => !getYoutubeEmbedUrl(url));
    return { images, video };
  }, [property]);

  const status = property?.status;
  const isDeleted = status === 'Eliminado' || property?.es_eliminado === true;
  const isInactive = !isDeleted && status !== 'Activo' && status !== 'Actualizado' && status !== 'ActualizadoCodigo';
  const isUpdated = status === 'Actualizado' || status === 'ActualizadoCodigo';

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-10">
        <SpinnerIcon className="w-16 h-16 text-sky-600 mb-6" />
        <p className="font-black uppercase tracking-widest text-gray-400 text-sm">Cargando portafolio...</p>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-10">
        <ExclamationTriangleIcon className="w-20 h-20 text-red-500 mb-6" />
        <p className="text-xl font-black text-gray-800 uppercase tracking-tighter mb-4">{error}</p>
        <button onClick={() => { window.location.href = '/'; }} className="px-8 py-3 bg-gray-900 text-white rounded-xl font-bold uppercase text-xs">
          Volver al inicio
        </button>
      </div>
    );
  }

  const searchParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1] || '');
  const brokerName = searchParams.get('bn') || searchParams.get('bName');
  const brokerPhone = searchParams.get('bp') || searchParams.get('bPhone');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <header className="bg-white shadow-sm sticky top-0 z-40 p-4 border-b border-gray-100">
        <div className="max-w-4xl mx-auto"><CienCuadrasLogoImg className="h-10 sm:h-12" /></div>
      </header>

      <main className="flex-grow max-w-4xl mx-auto w-full px-4 sm:px-6 py-10 space-y-12">
        {brokerName && <BrokerBusinessCard name={brokerName} phone={brokerPhone || undefined} />}

        <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden relative border border-gray-100">
          {(isInactive || isDeleted) && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-[2px] pointer-events-none">
              <div className={`text-white font-black text-3xl sm:text-5xl px-12 py-6 rounded-3xl transform -rotate-12 shadow-2xl border-4 border-white opacity-95 ${isDeleted ? 'bg-red-600' : 'bg-orange-500'}`}>
                {isDeleted ? 'ELIMINADO' : 'INACTIVO'}
              </div>
            </div>
          )}

          <div className={`p-4 sm:p-10 space-y-10 ${isInactive || isDeleted ? 'filter grayscale opacity-40' : ''}`}>
            {isUpdated && (
              <div className="bg-sky-600 text-white text-center py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg mb-8">
                Inmueble actualizado • Consulta el nuevo inventario
              </div>
            )}
            <SharedPropertyGallery images={media.images} video={media.video} activeIndex={activeIndex} onChangeIndex={setActiveIndex} />
            <SharedPropertyHeader property={property} />
            <SharedPropertyInfo property={property} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
